import { useEffect, useState } from 'react';

/**
 * Minimal listener surface — matches both PartySocket and the typed
 * useAgent return. Avoids importing `ReturnType<typeof useAgent>` here
 * because that resolves to the typed-overload return shape and
 * conflicts with the untyped one we actually get from `useAgent(...)`
 * called without an explicit type parameter.
 */
interface AgentListenable {
  addEventListener: (
    type: 'message',
    listener: (event: MessageEvent) => void,
  ) => void;
  removeEventListener: (
    type: 'message',
    listener: (event: MessageEvent) => void,
  ) => void;
}

/**
 * Bluebird's `_exec_stream` and `_watch` sandbox tools call
 * `this.broadcast(JSON.stringify(event))` on the DO (DD-CFS-18). The
 * agents SDK forwards every broadcast to ALL connected sockets for the
 * SAME DO instance, so a tab listening here sees stdout/stderr chunks,
 * file watch events, and tool errors from any tab in the same Bluebird
 * DO. We tag each entry with the receive timestamp + an incrementing
 * id so React can key the rendered list deterministically.
 *
 * The sink is bounded — older entries are evicted past `maxEvents` so
 * a long-running watch can't OOM the page.
 */
export interface LiveEvent {
  id: number;
  receivedAt: number;
  /** The raw `SandboxLiveEvent` JSON from the Worker. */
  data: unknown;
}

export function useLiveEvents(
  agent: AgentListenable,
  options?: { maxEvents?: number },
): LiveEvent[] {
  const max = options?.maxEvents ?? 200;
  const [events, setEvents] = useState<LiveEvent[]>([]);

  useEffect(() => {
    let nextId = 0;
    const onMessage = (e: MessageEvent) => {
      // Skip non-string frames (binary, etc.).
      if (typeof e.data !== 'string') return;
      // The chat stream uses framed protocol envelopes; live broadcasts
      // are plain JSON objects with a `type` field starting with
      // `sandbox.` (DD-CFS-18). Cheap discriminator before parsing.
      if (!e.data.startsWith('{"type":"sandbox.')) return;

      let parsed: unknown;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        return;
      }

      setEvents((prev) => {
        const next = prev.concat({
          id: nextId++,
          receivedAt: Date.now(),
          data: parsed,
        });
        if (next.length > max) next.splice(0, next.length - max);
        return next;
      });
    };

    agent.addEventListener('message', onMessage);
    return () => agent.removeEventListener('message', onMessage);
  }, [agent, max]);

  return events;
}
